import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { imageUrlToDataUrl, inlineImagesInElement } from './imageToDataUrl';

function escapeHtml(text) {
  if (text == null || text === '') return '';
  const d = document.createElement('div');
  d.textContent = String(text);
  return d.innerHTML;
}

function escapeAttr(text) {
  if (text == null || text === '') return '';
  // Data URLs must keep +, /, =; only escape quotes and angle brackets.
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatSignedAt(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * Standalone HTML for a signed/draft contract PDF (no app chrome).
 */
export function buildContractHtmlDocument(contract) {
  const title = escapeHtml(contract?.title || 'Service Agreement');
  const number = escapeHtml(contract?.contract_number || '');
  const vendorName = escapeHtml(
    contract?.vendor?.business_name || contract?.vendor_name || ''
  );
  const logoUrl = contract?.logo_url || contract?.vendor?.logo_url || '';
  const clientName = escapeHtml(contract?.client_name || '');
  const signerName = escapeHtml(contract?.signer_name || '');
  const signedAt = escapeHtml(formatSignedAt(contract?.signed_at));
  const body = contract?.body_html || '<p></p>';
  const signature = contract?.signature_data || '';
  const isSigned = String(contract?.status || '').toLowerCase() === 'signed' && signature;

  const signatureBlock = isSigned
    ? `
      <div class="sig">
        <p class="sig-label">Signature</p>
        <p class="sig-meta">Signed by <strong>${signerName}</strong>${
          signedAt ? ` on ${signedAt}` : ''
        }</p>
        <img class="sig-img" src="${signature}" alt="Signature" />
      </div>`
    : '';

  const logoBlock = logoUrl
    ? `<img class="logo" src="${escapeAttr(logoUrl)}" alt="" />`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>${title}</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 32px 40px;
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #111827;
    line-height: 1.55;
    background: #fff;
    width: 794px;
  }
  .logo {
    display: block;
    max-height: 56px;
    max-width: 180px;
    width: auto;
    height: auto;
    object-fit: contain;
    margin: 0 0 16px;
  }
  .meta {
    color: #6b7280;
    font-size: 12px;
    margin: 0 0 4px;
  }
  h1 {
    font-size: 22px;
    margin: 0 0 20px;
    font-weight: 700;
    color: #111827;
  }
  .body {
    font-size: 14px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .body p { margin: 0 0 0.75em; }
  .body ul, .body ol { margin: 0 0 0.75em; padding-left: 1.25em; }
  .body h1, .body h2, .body h3 { margin: 1em 0 0.5em; font-weight: 700; }
  .body img { max-width: 100%; height: auto; }
  .sig {
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
  }
  .sig-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin: 0 0 8px;
  }
  .sig-meta { font-size: 13px; margin: 0 0 12px; color: #374151; }
  .sig-img {
    display: block;
    max-width: 280px;
    height: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
  }
</style>
</head>
<body>
  ${logoBlock}
  ${vendorName ? `<p class="meta">${vendorName}</p>` : ''}
  ${number ? `<p class="meta">${number}${clientName ? ` · ${clientName}` : ''}</p>` : ''}
  <h1>${title}</h1>
  <div class="body">${body}</div>
  ${signatureBlock}
</body>
</html>`;
}

function saveCanvasAsPdf(canvas, filename) {
  const imgData = canvas.toDataURL('image/png', 1.0);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const contentWidth = pageWidth - margin * 2;
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;

  pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight + margin;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;
  }

  pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

/**
 * Build contract HTML off-screen and download a real PDF file (no print dialog).
 */
export async function downloadContractPdf(contract) {
  if (!contract) throw new Error('Nothing to export');

  const number = contract.contract_number || contract.id || 'export';
  const filename = `contract-${number}.pdf`;

  const rawLogo = contract?.logo_url || contract?.vendor?.logo_url || '';
  const logoDataUrl = rawLogo && !String(rawLogo).startsWith('data:')
    ? await imageUrlToDataUrl(rawLogo)
    : rawLogo || null;

  const html = buildContractHtmlDocument({
    ...contract,
    logo_url: logoDataUrl || null,
    vendor: contract?.vendor
      ? { ...contract.vendor, logo_url: logoDataUrl || contract.vendor.logo_url }
      : contract?.vendor,
  });

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'contract-pdf-export';
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;min-height:1123px;border:0;margin:0;padding:0;opacity:0;pointer-events:none;background:#fff';

  document.body.appendChild(iframe);

  try {
    const idoc = iframe.contentDocument;
    if (!idoc) throw new Error('Could not create export frame');

    idoc.open();
    idoc.write(html);
    idoc.close();

    await inlineImagesInElement(idoc.body);

    if (idoc.fonts?.ready) {
      try {
        await idoc.fonts.ready;
      } catch {
        /* ignore */
      }
    }
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 120)));

    const body = idoc.body;
    if (!body) throw new Error('Export frame has no body');

    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: body.scrollWidth,
      height: body.scrollHeight,
      windowWidth: body.scrollWidth,
      windowHeight: body.scrollHeight,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        const b = clonedDoc.body;
        if (b) {
          b.style.overflow = 'visible';
          b.style.height = 'auto';
        }
      },
    });

    saveCanvasAsPdf(canvas, filename);
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}

/**
 * @deprecated Prefer downloadContractPdf(contract) — keeps API for callers that pass a DOM node.
 */
export async function downloadContractPdfFromElement(_element, contract) {
  await downloadContractPdf(contract);
}
